import { ICallerIdService } from '../interfaces/ICallerIdService';
import { CallerIdLineType, ICallerIdResponse, ILineSelectionInfo } from '../types';
import { getMultiLineLabel } from '../../../packages/widgets/src/utils/handleMultiLineLabel';
import { Logger } from '../../../packages/logger/src';
import { setUcmLineCount } from '../utils/ucmLineCountCache';

/**
 * Shape of a single line returned by the UC Config REST API.
 */
interface UcmLine {
  dnorpattern: string;   // DN like '+14928000001' or extension/pattern
  index: number;         // 1-based line index on the device
  label: string | null;  // Often empty in real data
}

/**
 * Shape of a single device returned by the UC Config REST API.
 */
interface UcmDevice {
  name: string;       // e.g. 'CSFheliosucm01'
  model: number;      // numeric device model code
  lines: UcmLine[];
}

/**
 * Top-level response body from the UC Config "lines for person" endpoint.
 * Some tenants return a per-device list; others return a flat `lines` array.
 */
interface UcmLinesApiResponse {
  devices?: UcmDevice[];
  lines?: UcmLine[];
}

function deviceLineCount(device: UcmDevice): number {
  return Array.isArray(device.lines) ? device.lines.length : 0;
}

/**
 * Pick the device whose line list should drive the line picker.
 * Uses the device with the most configured lines — works across CSF, mobile,
 * tablet, and desk-phone profiles without hardcoding a device model.
 * If several devices tie, keeps the first one in API order (stable).
 */
export function selectBestUcmDevice(devices: UcmDevice[]): UcmDevice | null {
  if (!devices.length) {
    return null;
  }

  const devicesWithLines = devices.filter((d) => deviceLineCount(d) > 0);
  const candidates = devicesWithLines.length > 0 ? devicesWithLines : devices;

  return candidates.reduce((best, current) =>
    deviceLineCount(current) > deviceLineCount(best) ? current : best
  );
}

export function resolveUcmLinesFromApiBody(apiBody: UcmLinesApiResponse | undefined): {
  ucmLines: UcmLine[];
  source: string;
} {
  const flatLines = apiBody?.lines && Array.isArray(apiBody.lines) ? apiBody.lines : [];
  if (flatLines.length > 0) {
    return { ucmLines: flatLines, source: 'top-level lines array' };
  }

  const devices = apiBody?.devices && Array.isArray(apiBody.devices) ? apiBody.devices : [];
  if (!devices.length) {
    return { ucmLines: [], source: 'no devices' };
  }

  const device = selectBestUcmDevice(devices);
  if (!device) {
    return { ucmLines: [], source: 'no selectable device' };
  }

  if (devices.length > 1) {
    Logger.info(
      `[LineSelection][UCM] User has ${devices.length} devices; ` +
      `selected "${device.name}" (model ${device.model}, ${deviceLineCount(device)} lines)`
    );
  }

  return {
    ucmLines: Array.isArray(device.lines) ? device.lines : [],
    source: `device "${device.name}"`,
  };
}

/**
 * Service for UCM (Unified CM / NATIVE_SIP_CALL_TO_UCM) multi-line, using the
 * UC Config REST API to fetch the user's configured line list.
 *
 * Endpoint:
 *   GET {ucmgmt-gateway}/v1/uc/config/people/{userId}/lines?orgId={orgId}
 *
 * Implements ICallerIdService — drop-in replacement for PhoneServicesLineService
 * when the calling backend is UCM. The UI, hook, and selection-persistence layers
 * are entirely backend-agnostic and require no changes.
 *
 * Caveats:
 * - UCM lines have no GUID; cross-launch uses lineIndex only (lineId is omitted).
 * - Response is per-device for most tenants; some return a flat `lines` array.
 *   When multiple devices are present, use the device with the most configured
 *   lines (no device-model hardcoding).
 * - There is no Mercury push event for UCM line changes. External-change detection
 *   relies on the hook's existing visibility/online listeners.
 *
 * Ref: UCM_LINE_SELECTION_DESIGN.md
 */
export class UcmLineService implements ICallerIdService {

  async getLines(): Promise<ICallerIdResponse> {
    try {
      const ucmgmtGateway = window.webex?.internal?.services?._serviceUrls?.['ucmgmt-gateway'];
      const userId = window.webex?.internal?.device?.userId;
      const orgId = window.webex?.internal?.device?.orgId;

      if (!ucmgmtGateway || !userId || !orgId) {
        Logger.error(
          `[LineSelection][UCM] Missing prerequisites — ` +
          `ucmgmt-gateway: ${!!ucmgmtGateway}, userId: ${!!userId}, orgId: ${!!orgId}`
        );
        setUcmLineCount(0);
        return { currentSelection: CallerIdLineType.DEFAULT_CLID, lines: [] };
      }

      // Encode dynamic URL components defensively even though userId/orgId are
      // server-generated UUIDs in practice. This prevents accidental URL injection
      // if upstream sources ever change.
      const safeUserId = encodeURIComponent(userId);
      const safeOrgId = encodeURIComponent(orgId);
      const url = `${ucmgmtGateway}/v1/uc/config/people/${safeUserId}/lines?orgId=${safeOrgId}`;
      Logger.info(`[LineSelection][UCM] Fetching UCM lines from ${ucmgmtGateway}/v1/uc/config/people/<userId>/lines`);

      const response = await window.webex.request({ uri: url, method: 'GET' });
      const apiBody = response?.body as UcmLinesApiResponse | undefined;
      return this.mapApiBodyToResponse(apiBody);

    } catch (err) {
      Logger.error(`[LineSelection][UCM] Failed to fetch UCM lines: ${err}`);
      // Do NOT clear the cached count on transient errors — preserving the
      // last known count keeps call-history line badges working across brief
      // outages. A clean clear only happens on confirmed "no lines" responses.
      return { currentSelection: CallerIdLineType.DEFAULT_CLID, lines: [] };
    }
  }

  /**
   * Resolve, sort, and map a UC Config lines response body into the picker's
   * line list, and cache the resulting line count.
   */
  private mapApiBodyToResponse(apiBody: UcmLinesApiResponse | undefined): ICallerIdResponse {
    const { ucmLines, source } = resolveUcmLinesFromApiBody(apiBody);

    if (ucmLines.length === 0) {
      Logger.info(`[LineSelection][UCM] No lines returned from UC Config (${source})`);
      setUcmLineCount(0);
      return { currentSelection: CallerIdLineType.DEFAULT_CLID, lines: [] };
    }

    Logger.debug(
      `[LineSelection][UCM] Resolved ${ucmLines.length} lines from ${source}`
    );
    ucmLines.forEach((l, i) => {
      Logger.debug(
        `[LineSelection][UCM]   Line ${i}: index=${l.index}, ` +
        `dn="${l.dnorpattern}", label="${l.label ?? ''}"`
      );
    });

    // UC Config returns lines in arbitrary order. Sort by the authoritative
    // `index` field so the dropdown displays L1, L2, L3 sequentially regardless
    // of API ordering. Also aligns our 0-based `lineIndex` with UCM's index
    // ordering, so the cross-launch URL's lineIndex parameter maps cleanly.
    const sortedLines = [...ucmLines].sort(
      (a, b) => (a?.index ?? 0) - (b?.index ?? 0)
    );

    const lines: ILineSelectionInfo[] = sortedLines.map((line, idx) => {
      // Trim & test for a meaningful label. When the line has no label,
      // show the DN as the primary text and leave `number` empty so the
      // renderer doesn't print the same DN twice (one as title, one as number).
      const trimmedLabel = (line.label ?? '').trim();
      const hasLabel = trimmedLabel.length > 0;
      return {
        lineIndex: idx,
        lineLabel: getMultiLineLabel(line.index),
        title: hasLabel ? trimmedLabel : line.dnorpattern,
        number: hasLabel ? line.dnorpattern : '',
        lineType: idx === 0
          ? CallerIdLineType.DEFAULT_CLID
          : CallerIdLineType.ADDITIONAL_NUMBER,
        isSelected: idx === 0,
        lineGuid: undefined, // UCM has no GUID — cross-launch uses lineIndex only
      };
    });

    // Cache the count for WebexCallHistoryAdapter's hasMultipleLines computation.
    setUcmLineCount(lines.length);

    Logger.info(
      `[LineSelection][UCM] Mapped ${lines.length} lines, ` +
      `defaulting to L1 (${lines[0]?.title || 'N/A'})`
    );
    return { currentSelection: CallerIdLineType.DEFAULT_CLID, lines };
  }

  async setLine(line: ILineSelectionInfo): Promise<void> {
    // No-op for UCM: selection lives only in React state (useLineSelection hook)
    // and is persisted across reloads via setSelectedLineForCrossLaunch() in
    // selectedLineStore. There is no UCM endpoint to set the preferred line
    // server-side; Webex Desktop reads lineIndex from the webextel:// URL.
    Logger.info(
      `[LineSelection][UCM] setLine: ${line.lineLabel} (${line.title}) — ` +
      `session-only, propagated via cross-launch URL`
    );
  }
}
